import { useMemo } from 'react';

import { useGetLatestOversightRatingsByDocumentIds } from '@/hooks/queries';
import {
  isMoreRecentByTestDate,
  toTimestampOrNull,
} from '@/utils/oversightUtils';

export type OversightDocumentRatingLeaf = {
  Rating: number | null;
  CreatedAt: string | null;
};

export type OversightRatingsByDocument = Map<
  string,
  OversightDocumentRatingLeaf
>;

const EMPTY_MAP: OversightRatingsByDocument = new Map();

/**
 * Fetches the latest oversight rating per document for the given document Ids,
 * keyed by DocumentId.
 *
 * "Latest" mirrors the document page exactly
 * (getLatestOversightAssessmentDocumentAssessmentResultByDocumentId): the
 * oversight result with the newest `TestDate` (tie-broken by
 * `CreatedAtTimestamp`), both read from the result row itself. There is no
 * completion gate — any oversight assessment result counts, so the rating
 * surfaces as soon as it is assigned.
 *
 * Like risks and obligations (and unlike controls), `document_oversight_result`
 * has no direct ParentDocumentId column — a finding can attach to multiple
 * documents via the `parents` array_relationship. `documentParent` is
 * server-filtered to ParentId ∈ $DocumentIds; the client fans the leaf out
 * across each matching parent.
 *
 * Data fetching (GraphQL / tRPC feature-flag switch) is delegated to the
 * `useGetLatestOversightRatingsByDocumentIds` bridge hook; this hook owns the
 * "latest per document" reduction the register consumes.
 *
 * Skips the query when oversight viewing is disabled or no documents are
 * visible.
 */
export const useLatestOversightRatingsByDocuments = (params: {
  documentIds: string[];
  isEnabled: boolean;
}): {
  ratingsByDocument: OversightRatingsByDocument;
  loading: boolean;
} => {
  const { documentIds, isEnabled } = params;
  const skip = !isEnabled || documentIds.length === 0;

  const { data, loading } = useGetLatestOversightRatingsByDocumentIds({
    queryArgs: { documentIds },
    shouldSkip: skip,
  });

  const ratingsByDocument = useMemo<OversightRatingsByDocument>(() => {
    if (!data) {
      return EMPTY_MAP;
    }

    const latestByDocument = new Map<
      string,
      { leaf: OversightDocumentRatingLeaf; TestDate: string | null }
    >();

    for (const row of data.document_oversight_result) {
      const testDate = toTimestampOrNull(row.TestDate);
      const createdAt = toTimestampOrNull(row.CreatedAtTimestamp);
      const leaf: OversightDocumentRatingLeaf = {
        Rating: row.Rating ?? null,
        CreatedAt: createdAt,
      };
      for (const dp of row.documentParent) {
        const existing = latestByDocument.get(dp.ParentId);
        if (
          !existing ||
          isMoreRecentByTestDate(
            {
              TestDate: existing.TestDate,
              CreatedAt: existing.leaf.CreatedAt,
            },
            { TestDate: testDate, CreatedAt: createdAt }
          )
        ) {
          latestByDocument.set(dp.ParentId, { leaf, TestDate: testDate });
        }
      }
    }

    const result: OversightRatingsByDocument = new Map();
    for (const [id, entry] of latestByDocument) {
      result.set(id, entry.leaf);
    }

    return result;
  }, [data]);

  return {
    ratingsByDocument,
    loading,
  };
};
